<template>
  <div class="w-full" :class="{ 'dialog-context': insideDialog }">
    <!-- Header Tabs -->
    <div v-if="!$vuetify.breakpoint.smAndDown" class="border-b border-gray-300 sticky top-0 z-10 bg-white">
      <div class="p-1">
        <div class="flex justify-between items-center w-full min-w-0">
          <div class="flex space-x-2 overflow-x-auto hide-scrollbar min-w-0 flex-1 mr-4 pb-1">
            <v-btn
              v-for="(tab, index) in tabs"
              :key="index"
              :color="activeTab === tab.key ? 'primary' : 'gray'"
              @click="setActiveTab(tab.key)"
              depressed
              class="text-xs font-semibold flex-shrink-0"
              :outlined="activeTab === tab.key ? false : true"
              :class="activeTab === tab.key ? 'text-white' : ''"
            >
              <v-icon left>{{ tab.icon }}</v-icon>
              {{ tab.label }}
            </v-btn>
          </div>
          <!-- Filter Buttons for Each Tab -->
          <div class="h-10">
            <slot name="tab-buttons" :activeTab="activeTab"></slot>
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile Header Tabs -->
    <ActitiveTabcomponent_mobile
      v-else
      :tabs="tabs"
      :active-tab="activeTab"
      :inside-dialog="insideDialog"
      @tab-changed="setActiveTab"
    >
      <template v-slot:tab-buttons="{ activeTab: mobileActiveTab }">
        <slot name="tab-buttons" :activeTab="mobileActiveTab"></slot>
      </template>
    </ActitiveTabcomponent_mobile>

    <!-- Dynamic Content Based on Active Tab -->
    <div class="tab-content" :style="dynamicHeightStyle">
      <div
        v-if="
          activeTabObject &&
          activeTabObject.permissionKey &&
          !hasIamPermission(activeTabObject.permissionKey)
        "
        class="pa-10 text-center grey--text"
      >
        <v-icon size="64" color="grey lighten-2">mdi-lock-outline</v-icon>
        <div class="text-subtitle-1 font-weight-bold uppercase mt-4">Access Denied</div>
        <div class="text-caption">You do not have permission to view this tab's content.</div>
      </div>
      <slot v-else :name="activeTab" :activeTab="activeTab"></slot>
    </div>
  </div>
</template>

<script>
import { hasIamPermission } from '@/utils/iamEvaluator'
import ActitiveTabcomponent_mobile from './ActitiveTabcomponent_mobile.vue'

export default {
  name: 'ActiveTabComponent',
  components: {
    ActitiveTabcomponent_mobile
  },
  props: {
    tabs: {
      type: Array,
      required: true,
      validator: (tabs) => {
        return tabs.every(
          (tab) =>
            Object.prototype.hasOwnProperty.call(tab, 'key') &&
            Object.prototype.hasOwnProperty.call(tab, 'label') &&
            Object.prototype.hasOwnProperty.call(tab, 'icon') &&
            Object.prototype.hasOwnProperty.call(tab, 'component')
        )
      }
    },
    defaultTab: {
      type: String,
      default: null
    },
    insideDialog: {
      type: Boolean,
      default: false,
      description:
        'Set to true when component is used inside a dialog to prevent conflicting scrollbars'
    }
  },
  data() {
    return {
      activeTab: this.defaultTab || (this.tabs.length > 0 ? this.tabs[0].key : null),
      headerHeight: 120, // Default header height
      windowHeight: window.innerHeight
    }
  },
  computed: {
    dynamicHeightStyle() {
      // If inside dialog, let the dialog handle the height and scrolling
      if (this.insideDialog) {
        return {
          height: 'auto',
          maxHeight: 'none',
          overflow: 'hidden'
        }
      }

      // Calculate available height dynamically with zoom handling for main content
      // Use visual viewport for better zoom support
      const viewportHeight = window.visualViewport
        ? window.visualViewport.height
        : window.innerHeight
      const availableHeight = Math.max(viewportHeight - this.headerHeight - 70, 300) // Minimum 300px

      return {
        maxHeight: `${availableHeight}px`,
        overflowY: 'auto',
        overflowX: 'hidden',
        position: 'relative'
      }
    },
    activeTabObject() {
      return this.tabs.find((t) => t.key === this.activeTab)
    },
    iamStatements() {
      return this.$store.state.wfmStatements || []
    }
  },
  mounted() {
    // Skip viewport calculations if inside dialog
    if (this.insideDialog) {
      return
    }

    this.updateDimensions()

    // Add multiple event listeners for better responsiveness
    window.addEventListener('resize', this.updateDimensions)
    window.addEventListener('orientationchange', this.updateDimensions)

    // Handle visual viewport changes (zoom, mobile keyboard, etc.)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.updateDimensions)
      window.visualViewport.addEventListener('scroll', this.updateDimensions)
    }

    // Calculate actual header height after component is mounted
    this.$nextTick(() => {
      this.calculateHeaderHeight()
      // Recalculate after a short delay to ensure all elements are rendered
      setTimeout(() => {
        this.calculateHeaderHeight()
      }, 100)
    })
  },
  beforeDestroy() {
    // Skip cleanup if inside dialog
    if (this.insideDialog) {
      return
    }

    // Clean up all event listeners
    window.removeEventListener('resize', this.updateDimensions)
    window.removeEventListener('orientationchange', this.updateDimensions)

    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.updateDimensions)
      window.visualViewport.removeEventListener('scroll', this.updateDimensions)
    }
  },
  methods: {
    hasIamPermission(actionKey) {
      try {
        const userObj = JSON.parse(localStorage.getItem('user'))
        if (userObj) {
          const roles = userObj.role || userObj.cwRole || []
          if (roles.includes('WFM_ADMIN')) {
            return true
          }
        }
      } catch (e) {
        console.error('Error parsing user roles in active tab component:', e)
      }
      return hasIamPermission(this.iamStatements, actionKey)
    },
    setActiveTab(tabKey) {
      this.activeTab = tabKey
      this.$emit('tab-changed', tabKey)

      // Skip dimension recalculation if inside dialog
      if (this.insideDialog) {
        return
      }

      // Recalculate dimensions when tab changes as content might affect layout
      this.$nextTick(() => {
        this.updateDimensions()
      })
    },
    updateDimensions() {
      // Use visual viewport if available (better zoom support)
      if (window.visualViewport) {
        this.windowHeight = window.visualViewport.height
      } else {
        this.windowHeight = window.innerHeight
      }

      // Recalculate header height on dimension changes
      this.$nextTick(() => {
        this.calculateHeaderHeight()
      })
    },
    calculateHeaderHeight() {
      // Calculate the actual height of header elements more accurately
      try {
        const containerElement = this.$el
        const headerElement = this.$el.querySelector('.border-b')

        if (containerElement && headerElement) {
          const containerRect = containerElement.getBoundingClientRect()
          const headerRect = headerElement.getBoundingClientRect()

          // Calculate header height relative to container + some padding
          const calculatedHeight = headerRect.bottom - containerRect.top + 30

          // Use a reasonable range for header height (prevent extreme values)
          this.headerHeight = Math.max(80, Math.min(calculatedHeight, 200))
        } else {
          // Fallback if elements not found
          this.headerHeight = 120
        }
      } catch (error) {
        console.warn('Error calculating header height:', error)
        this.headerHeight = 120 // Safe fallback
      }
    }
  },
  watch: {
    defaultTab(newVal) {
      if (newVal) {
        this.activeTab = newVal
      }
    },
    insideDialog: {
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          this.activeTab = newVal[0].key
          this.$emit('tab-changed', newVal[0].key)
        }
      }
    }
  }
}
</script>

<style scoped>
.tab-content {
  transition: max-height 0.3s ease;
}

.dialog-context .tab-content {
  height: auto;
  max-height: none;
  overflow: visible;
  transition: none;
}

.v-btn {
  text-transform: none !important;
  color: #21427d;
}
.buttonClass {
  font-size: 12px;
}

.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
.hide-scrollbar {
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
}
</style>
